/**
 * FIELD-TEST F1 (Build 6) — `tayro.run/join/<code>` must open the APP.
 *
 * With the Associated Domains entitlement in place (founder-only, Build 7),
 * iOS hands the tapped universal link to the Capacitor App plugin as an
 * `appUrlOpen` event carrying the FULL https URL — not the custom
 * `tayro://` scheme. `lib/nativeAuth.ts` already owns the single
 * `appUrlOpen` listener for the auth callback, so this module is the pure
 * part it delegates to: URL in, invite code out.
 *
 * Two rules bind here.
 *
 * 1. **The 13+ posture from Wave 23: a link may never join anybody.** The
 *    only thing a deep link is allowed to do is write the SAME
 *    sessionStorage stash the logged-out web flow writes
 *    (`lib/team/joinStash.ts`) and let `<JoinCodeMount>` forward to
 *    `/team?join=<code>`, where <JoinTeamModal> asks for the explicit tap
 *    that has always been the thing that writes. Reusing the stash verbatim
 *    is deliberate: it means the deep link inherits the OAuth round-trip
 *    survival the web flow already has, so a signed-OUT athlete who taps an
 *    invite, signs in through the system browser and returns on
 *    `tayro://auth-callback` still lands on the confirmation.
 *
 * 2. **Only our own host, only the join path.** `appUrlOpen` is an OS-level
 *    entry point; anything that reaches it is untrusted input. A URL that is
 *    not `https://tayro.run/join/<code>` (or the `www.` alias) is ignored
 *    outright rather than coerced — an attacker-shaped path must not become
 *    a code we hand to the join API.
 *
 * `<JoinCodeMount>` reads the stash on mount and on session change, neither
 * of which happens when a link arrives at an app that is ALREADY open and
 * signed in — so a successful stash also fires `JOIN_STASH_EVENT` to wake it.
 */

import { stashJoinCode } from './joinStash';

/** The apex domain plus the `www.` alias Safari may hand through. */
const JOIN_LINK_HOSTS = new Set(['tayro.run', 'www.tayro.run']);

/** Exactly one path segment, in the server's own invite-code alphabet
 *  (see `generateInviteCode` / `joinStash`'s CODE_RE). A trailing slash is
 *  tolerated because messaging apps append one. */
const JOIN_PATH_RE = /^\/join\/([A-Za-z0-9_-]{1,64})\/?$/;

/**
 * Fired on `window` after a deep link stashes a code, so a mounted
 * `<JoinCodeMount>` can consume it without waiting for a navigation.
 */
export const JOIN_STASH_EVENT = 'tayro:join-code-stashed';

/**
 * Pull the invite code out of a universal link. Returns null for anything
 * that is not one of ours — including the `tayro://auth-callback` deep link,
 * which stays entirely the auth listener's business.
 */
export function parseJoinDeepLink(url: string | null | undefined): string | null {
  if (!url) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  // Universal links are https by definition; http would be a downgrade we
  // never asked Apple to associate.
  if (parsed.protocol !== 'https:') return null;
  if (!JOIN_LINK_HOSTS.has(parsed.hostname.toLowerCase())) return null;
  const match = JOIN_PATH_RE.exec(parsed.pathname);
  return match ? match[1] : null;
}

/**
 * Parse + stash + wake the consumer. Returns the code on a hit, null when
 * the URL was not a join link (the caller treats null as "not mine").
 *
 * Note what this does NOT do: it does not join, does not navigate, and does
 * not care whether anyone is signed in. The stash is inert until
 * `<JoinCodeMount>` sees a session.
 */
export function handleJoinDeepLink(url: string | null | undefined): string | null {
  const code = parseJoinDeepLink(url);
  if (!code) return null;
  stashJoinCode(code);
  try {
    window.dispatchEvent(new Event(JOIN_STASH_EVENT));
  } catch {
    /* no window (or Event unavailable) — the stash still stands and the
       next mount/session change consumes it */
  }
  return code;
}
